import React, { useEffect, useState, createContext, useContext } from 'react';
interface CurrencyInfo {
  code: string;
  symbol: string;
  rate: number; // rate relative to USD
}
interface CurrencyContextType {
  currency: CurrencyInfo;
  basePrice: number;
  formattedPrice: string;
  isLoading: boolean;
}
const CURRENCIES: Record<string, CurrencyInfo> = {
  USD: {
    code: 'USD',
    symbol: '$',
    rate: 1
  },
  CAD: {
    code: 'CAD',
    symbol: 'CA$',
    rate: 1.38
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    rate: 0.92
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    rate: 0.79
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    rate: 1.55
  },
  MXN: {
    code: 'MXN',
    symbol: 'MX$',
    rate: 17.15
  },
  BRL: {
    code: 'BRL',
    symbol: 'R$',
    rate: 5.05
  },
  COP: {
    code: 'COP',
    symbol: 'COL$',
    rate: 4150
  },
  ARS: {
    code: 'ARS',
    symbol: 'AR$',
    rate: 875
  },
  CLP: {
    code: 'CLP',
    symbol: 'CL$',
    rate: 940
  },
  PEN: {
    code: 'PEN',
    symbol: 'S/',
    rate: 3.72
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    rate: 83.5
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    rate: 149.5
  },
  CNY: {
    code: 'CNY',
    symbol: '¥',
    rate: 7.24
  },
  KRW: {
    code: 'KRW',
    symbol: '₩',
    rate: 1330
  },
  SEK: {
    code: 'SEK',
    symbol: 'kr',
    rate: 10.85
  },
  NOK: {
    code: 'NOK',
    symbol: 'kr',
    rate: 10.75
  },
  DKK: {
    code: 'DKK',
    symbol: 'kr',
    rate: 6.88
  },
  CHF: {
    code: 'CHF',
    symbol: 'CHF',
    rate: 0.88
  },
  NZD: {
    code: 'NZD',
    symbol: 'NZ$',
    rate: 1.68
  },
  PLN: {
    code: 'PLN',
    symbol: 'zł',
    rate: 4.02
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    rate: 18.5
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    rate: 1.34
  },
  HKD: {
    code: 'HKD',
    symbol: 'HK$',
    rate: 7.82
  },
  PHP: {
    code: 'PHP',
    symbol: '₱',
    rate: 56.5
  },
  THB: {
    code: 'THB',
    symbol: '฿',
    rate: 35.5
  },
  TWD: {
    code: 'TWD',
    symbol: 'NT$',
    rate: 31.8
  },
  ILS: {
    code: 'ILS',
    symbol: '₪',
    rate: 3.65
  },
  TRY: {
    code: 'TRY',
    symbol: '₺',
    rate: 32.5
  }
};
// Map country codes to currency codes
const COUNTRY_TO_CURRENCY: Record<string, string> = {
  US: 'USD',
  CA: 'CAD',
  GB: 'GBP',
  IE: 'EUR',
  DE: 'EUR',
  FR: 'EUR',
  ES: 'EUR',
  IT: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  AT: 'EUR',
  PT: 'EUR',
  FI: 'EUR',
  GR: 'EUR',
  LU: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  EE: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  MT: 'EUR',
  CY: 'EUR',
  HR: 'EUR',
  AU: 'AUD',
  NZ: 'NZD',
  MX: 'MXN',
  BR: 'BRL',
  CO: 'COP',
  AR: 'ARS',
  CL: 'CLP',
  PE: 'PEN',
  IN: 'INR',
  JP: 'JPY',
  CN: 'CNY',
  KR: 'KRW',
  SE: 'SEK',
  NO: 'NOK',
  DK: 'DKK',
  CH: 'CHF',
  PL: 'PLN',
  ZA: 'ZAR',
  SG: 'SGD',
  HK: 'HKD',
  PH: 'PHP',
  TH: 'THB',
  TW: 'TWD',
  IL: 'ILS',
  TR: 'TRY'
};
const BASE_PRICE_USD = 6.39;
const DEFAULT_CURRENCY = CURRENCIES.USD;
function formatPrice(amount: number, currency: CurrencyInfo): string {
  // For currencies with large values, round to nearest whole number
  const highValueCurrencies = ['JPY', 'KRW', 'COP', 'ARS', 'CLP', 'INR', 'PHP', 'THB', 'TWD', 'TRY'];
  let formatted: number;
  if (highValueCurrencies.includes(currency.code)) {
    formatted = Math.round(amount);
  } else {
    formatted = Math.round(amount * 100) / 100;
  }
  // Format with locale-appropriate separators
  const formattedNumber = highValueCurrencies.includes(currency.code) ? formatted.toLocaleString('en-US', {
    maximumFractionDigits: 0
  }) : formatted.toFixed(2);
  return `${currency.symbol}${formattedNumber} ${currency.code}`;
}
const CurrencyContext = createContext<CurrencyContextType>({
  currency: DEFAULT_CURRENCY,
  basePrice: BASE_PRICE_USD,
  formattedPrice: formatPrice(BASE_PRICE_USD, DEFAULT_CURRENCY),
  isLoading: true
});
export function CurrencyProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currency, setCurrency] = useState<CurrencyInfo>(DEFAULT_CURRENCY);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    async function detectCurrency() {
      try {
        // Use a free IP geolocation service
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(3000)
        });
        if (!response.ok) throw new Error('Geo lookup failed');
        const data = await response.json();
        const countryCode = data.country_code;
        if (countryCode) {
          const currencyCode = COUNTRY_TO_CURRENCY[countryCode];
          if (currencyCode && CURRENCIES[currencyCode]) {
            setCurrency(CURRENCIES[currencyCode]);
          }
        }
      } catch {
        // Silently fall back to USD
      } finally {
        setIsLoading(false);
      }
    }
    detectCurrency();
  }, []);
  const convertedPrice = BASE_PRICE_USD * currency.rate;
  const formattedPrice = formatPrice(convertedPrice, currency);
  return <CurrencyContext.Provider value={{
    currency,
    basePrice: BASE_PRICE_USD,
    formattedPrice,
    isLoading
  }} data-id="element-232">
      {children}
    </CurrencyContext.Provider>;
}
export function useCurrency() {
  return useContext(CurrencyContext);
}